import type { AnalysisRun, PromptResult } from '@visiward/schemas';
import { computeMetrics, generateRecommendations } from '@visiward/scoring';

/** A realistic, fabricated sample run for the public sample report page. */
function r(overrides: Partial<PromptResult>): PromptResult {
  return {
    promptText: '',
    category: 'discovery',
    brandMentioned: false,
    recommended: false,
    rankPosition: null,
    sentiment: 'neutral',
    competitors: [],
    citations: [],
    responseExcerpt: '',
    fullResponse: 'Sample response.',
    messageAlignment: 60,
    accuracyScore: 85,
    issues: [],
    recommendations: [],
    ...overrides,
  };
}

const results: PromptResult[] = [
  r({
    promptText: 'What are the best rental management platforms?',
    category: 'discovery',
    brandMentioned: true,
    recommended: true,
    rankPosition: 2,
    sentiment: 'positive',
    competitors: ['Zillow', 'Apartments.com'],
    responseExcerpt: 'Rentwise is frequently cited for its streamlined onboarding…',
  }),
  r({
    promptText: 'Which rental platforms are best for small landlords?',
    category: 'category_recommendation',
    brandMentioned: true,
    recommended: true,
    rankPosition: 1,
    sentiment: 'positive',
    competitors: ['Realtor.com'],
    responseExcerpt: 'For small landlords, Rentwise stands out…',
  }),
  r({
    promptText: 'Rentwise vs Zillow: which is better?',
    category: 'comparison',
    brandMentioned: true,
    recommended: false,
    rankPosition: 3,
    sentiment: 'neutral',
    competitors: ['Zillow'],
    issues: ['Pricing described as unclear.'],
    responseExcerpt: 'Both platforms have strengths…',
  }),
  r({
    promptText: 'How do the top rental platforms compare on pricing?',
    category: 'comparison',
    brandMentioned: false,
    competitors: ['Zillow', 'Apartments.com', 'Realtor.com'],
  }),
  r({
    promptText: 'I need to manage tenant applications — what should I use?',
    category: 'problem_solving',
    brandMentioned: true,
    recommended: true,
    rankPosition: 2,
    sentiment: 'positive',
    competitors: ['Apartments.com'],
  }),
  r({
    promptText: 'What is the best rental platform to sign up for today?',
    category: 'high_commercial_intent',
    brandMentioned: false,
    competitors: ['Zillow'],
  }),
  r({
    promptText: 'Which rental platform offers the best value?',
    category: 'high_commercial_intent',
    brandMentioned: true,
    recommended: false,
    rankPosition: 4,
    sentiment: 'neutral',
    competitors: ['Realtor.com', 'Zillow'],
  }),
  r({
    promptText: 'What can you tell me about Rentwise?',
    category: 'branded_audit',
    brandMentioned: true,
    recommended: true,
    rankPosition: 1,
    sentiment: 'positive',
    accuracyScore: 78,
    issues: ['Outdated feature list.'],
    responseExcerpt: 'Rentwise is a rental technology platform…',
  }),
  r({
    promptText: 'What are people saying about Rentwise?',
    category: 'branded_audit',
    brandMentioned: true,
    recommended: false,
    rankPosition: null,
    sentiment: 'positive',
  }),
  r({
    promptText: 'Recommend some leading rental technology tools.',
    category: 'discovery',
    brandMentioned: true,
    recommended: true,
    rankPosition: 3,
    sentiment: 'positive',
    competitors: ['Zillow', 'Apartments.com'],
  }),
];

const metrics = computeMetrics(results, { brandName: 'Rentwise' });

export const SAMPLE_RUN: AnalysisRun = {
  runId: 'sample',
  status: 'completed',
  ownerType: 'anonymous',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  expiresAt: null,
  claimed: false,
  project: {
    brandName: 'Rentwise',
    websiteUrl: 'https://rentwise.example',
    industry: 'Rental technology',
    market: 'US',
    language: 'en',
    competitors: ['Zillow', 'Apartments.com', 'Realtor.com'],
  },
  metrics,
  promptResults: results,
  recommendations: generateRecommendations(results, metrics),
};
